import type {
  AdminInspectorDetailDto,
  AdminInspectorPackagePricingDto,
  AdminInspectorServicePricingDto,
} from '@carcheck/shared';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SectionTitle } from './InfoBlocks';
import { tokens } from '../../../design';
import { formatCurrency } from '../../../utils/format';

/**
 * Pricing tab of the inspector detail drawer (AUT-125). Renders the
 * embedded `pricing` summary off the detail DTO — services + packages,
 * each as a compact MUI table. No extra fetch (the pricing surface lives
 * inside the detail DTO via `PricingService.getInspectorPricing`).
 */

const ServicesTable = ({
  services,
  t,
}: {
  services: AdminInspectorServicePricingDto[];
  t: TFunction;
}): ReactElement => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>{t('inspectors.pricingTab.columns.serviceType')}</TableCell>
        <TableCell align="right">{t('inspectors.pricingTab.columns.price')}</TableCell>
        <TableCell align="right">{t('inspectors.pricingTab.columns.duration')}</TableCell>
        <TableCell align="center">{t('inspectors.pricingTab.columns.available')}</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {services.map((s) => (
        <TableRow key={s.serviceType}>
          <TableCell>
            {t(`inspectors.pricingTab.serviceTypes.${s.serviceType}`, {
              defaultValue: s.serviceType,
            })}
          </TableCell>
          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
            {formatCurrency(s.price)}
          </TableCell>
          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
            {t('inspectors.pricingTab.minutes', { count: s.durationMin })}
          </TableCell>
          <TableCell align="center">
            {s.available
              ? t('inspectors.pricingTab.available')
              : t('inspectors.pricingTab.unavailable')}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const PackagesTable = ({
  packages,
  t,
}: {
  packages: AdminInspectorPackagePricingDto[];
  t: TFunction;
}): ReactElement => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>{t('inspectors.pricingTab.columns.package')}</TableCell>
        <TableCell align="right">{t('inspectors.pricingTab.columns.regularPrice')}</TableCell>
        <TableCell align="right">{t('inspectors.pricingTab.columns.packagePrice')}</TableCell>
        <TableCell align="right">{t('inspectors.pricingTab.columns.savings')}</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {packages.map((p) => (
        <TableRow key={p.id}>
          <TableCell>{p.name}</TableCell>
          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
            {formatCurrency(p.regularPrice)}
          </TableCell>
          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
            {formatCurrency(p.packagePrice)}
          </TableCell>
          <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'success.main' }}>
            −{formatCurrency(p.savings)}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const PricingTab = ({ inspector }: { inspector: AdminInspectorDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const { services, packages } = inspector.pricing;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <SectionTitle>{t('inspectors.pricingTab.servicesSection')}</SectionTitle>
        {services.length === 0 ? (
          <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
            {t('inspectors.pricingTab.noServices')}
          </Typography>
        ) : (
          <ServicesTable services={services} t={t} />
        )}
      </Box>
      <Box>
        <SectionTitle>{t('inspectors.pricingTab.packagesSection')}</SectionTitle>
        {packages.length === 0 ? (
          <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
            {t('inspectors.pricingTab.noPackages')}
          </Typography>
        ) : (
          <PackagesTable packages={packages} t={t} />
        )}
      </Box>
    </Box>
  );
};
